import type { UserType } from '@/types'

/**
 * Where a user belongs when they have nowhere else to be.
 *
 * Two places need this and both were getting it wrong:
 *
 *   1. `App.tsx` renders the CRM dashboard at `/` with **no guard at all**, so any
 *      authenticated account could reach it. HR-only `Employee` users could
 *      already see it before Training existed.
 *   2. `ProtectedRoute` redirects a refused user to a hardcoded `/`. For an
 *      account with no CRM access that bounces them *into* the CRM — the one place
 *      they must not land.
 *
 * A restricted user type is only as contained as its fallback route, so the
 * fallback has to know about roles. Adding a user type here is the same act as
 * adding it to the union: if it has its own workspace, say so.
 */
export function workspaceHome(userType: UserType | string | null | undefined): string {
  switch (userType) {
    case 'Employee':
      return '/hr'
    case 'Training':
      return '/training'
    default:
      return '/'
  }
}

/**
 * True when this account has no business in the CRM at all — used to decide
 * whether `/` should render or redirect.
 */
export function isWorkspaceOnlyUser(userType: UserType | string | null | undefined): boolean {
  return workspaceHome(userType) !== '/'
}
